import { useApiHandler } from '@/hooks/useApiHandler'
import {
    ApiConfig,
    ApiResponse,
    MessageLevel,
    ErrorHandlingStrategy
} from '@/types/api'
import { ApiRoutes } from './apiRoutes'

// Payload para /homologations/by-document
export interface HomologationByDocumentRequest {
    idDocumentNumber: string
    academicPlanId: string | number
    originInstitutions: Array<{
        originInstitutionName: string
        programOriginName: string
    }>
}

export interface HomologationResponse {
    id: number
}

export interface HomologationUploadResponse {
    id: number
    idDocumentType: number
    idHomologationProcessInstitution: number
    documentUrl: string
    idOnbase: number
}

export function useHomologationsApi(config?: Partial<ApiConfig>) {
    const api = useApiHandler(ApiRoutes.HOMOLOGATIONS, config)

    // Instancia silenciosa para consultas que no deben mostrar toasts
    const silentApi = useApiHandler(ApiRoutes.HOMOLOGATIONS, {
        messageLevel: MessageLevel.NONE,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    // Helper interno: obtener URL del acta final por documento e inscripción
    const getFinalActByDocument = (
        documentNumber: string,
        inscriptionId?: number
    ): Promise<ApiResponse<string>> =>
        silentApi.get<string>(
            `/query/detail/by-document/${documentNumber}/acta-final`,
            inscriptionId !== undefined
                ? { params: { inscriptionId } }
                : undefined
        )

    const getEnrollmentSummaryByDocument = (
        documentNumber: string
    ): Promise<ApiResponse<any>> =>
        api.get<any>('/query/enrollments/summary', {
            params: {
                page: 0,
                size: 1,
                'sort[]': 'inscriptionId,desc',
                searchTerm: documentNumber
            }
        })

    const getEnrollmentOriginLatest = (
        documentNumber: string,
        idInscription: number,
        options?: { silent?: boolean }
    ): Promise<ApiResponse<any>> => {
        const client = options?.silent ? silentApi : api

        return client.get<any>('/query/enrollments/origin/latest', {
            params: {
                documentNumber,
                idInscription
            }
        })
    }

    return {
        getHomologationsByDocument: (
            data: HomologationByDocumentRequest
        ): Promise<ApiResponse<HomologationResponse>> =>
            api.post<HomologationResponse>('/homologations/by-document', data),
        // Nuevo método: sincronizar documentos del aspirante con homologaciones
        syncAspirantDocuments: (
            idAspirant: number,
            idInscription: number,
            idHomologations: number
        ): Promise<ApiResponse<any>> =>
            api.post<any>('/homologations/sync-aspirant-documents', undefined, {
                params: { idAspirant, idInscription, idHomologations }
            }),

        uploadHomologationDocument: (params: {
            idHomologation: number
            idDocumentType: number
            idHomologationProcessInstitution: number
            file: File
            overrideDocument: boolean
        }): Promise<ApiResponse<HomologationUploadResponse>> => {
            const formData = new FormData()

            formData.append('idHomologation', String(params.idHomologation))
            formData.append('idDocumentType', String(params.idDocumentType))
            formData.append(
                'idHomologationProcessInstitution',
                String(params.idHomologationProcessInstitution)
            )
            formData.append('file', params.file)
            formData.append('overrideDocument', String(params.overrideDocument))

            return api.post<HomologationUploadResponse>(
                '/homologations/upload-document',
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }
            )
        },
        getEnrollmentSummaryByDocument,
        getEnrollmentOriginLatest,
        // Obtener URL del acta final por documento y inscripción
        getFinalActByDocument
    }
}
